/* eslint-disable react/prop-types */
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { HTTP_STATUS } from "constants/HttpStatus";
import { HTTP_STATUS_MESSAGE } from "constants/HttpStatus";
import { leadColors } from "service/Common";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function LeadDetailsModal({ open, onClose, leadId }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const axiosPrivate = useAxiosPrivate();
  const [leadsDetails, setLeadsDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const getLeadDetails = (leadId) => {
    const option = {
      url: `om/lead-on-the-basis-of-leadId?leadId=${leadId}`,
      method: "get",
    };
    setLoading(true);
    axiosPrivate(option)
      .then((response) => {
        if (
          response?.status === HTTP_STATUS.OK &&
          response?.data?.status === HTTP_STATUS_MESSAGE.OK
        ) {
          setLeadsDetails(response?.data?.data);
        } else {
          setLeadsDetails({});
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (leadId) {
      getLeadDetails(leadId);
    }
  }, [leadId]);

  //   if (!leadId) return null;
  const InfoItem = ({ label, value }) => (
    <MDBox>
      <MDTypography variant="h6" color="text" fontWeight="small">
        {label} :{" "}
      </MDTypography>
      <MDTypography variant="caption" fontWeight="small">
        {value || ""}
      </MDTypography>
    </MDBox>
  );

  const CallLogCard = ({ log }) => {
    const isMissed = log?.duration_in_sec === 0;

    return (
      <MDBox
        sx={{
          border: "1px solid",
          borderColor: isMissed ? "#ef4444" : "#10b981",
          borderRadius: "12px",
          p: 2,
          backgroundColor: "rgba(0,0,0,0.02)",
        }}
      >
        <MDBox display="flex" justifyContent="space-between" mb={1}>
          <MDTypography variant="caption" fontWeight="bold" color={isMissed ? "error" : "success"}>
            {isMissed ? "Not Connected" : "Connected"}
          </MDTypography>

          <MDTypography variant="caption">
            {dayjs.unix(log.start_time).format("DD MMM YYYY, hh:mm A")}
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" mb={0.5}>
          Call By: <b>{log.call_by}</b>
        </MDTypography>

        <MDTypography variant="body2">
          Duration:{" "}
          <b>
            {isMissed
              ? "0 sec"
              : `${Math.floor(log.duration_in_sec / 60)}m ${log.duration_in_sec % 60}s`}
          </b>
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          color: darkMode ? "#fff" : "#000",
          borderRadius: 2,
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: darkMode ? "#fff" : "#000",
          backgroundColor: darkMode ? "#1f2937" : "#f5f5f5",
          borderBottom: `1px solid ${darkMode ? "#475894" : "#d9d9d9"}`,
        }}
      >
        <MDTypography variant="h5">Lead Details</MDTypography>

        <IconButton onClick={onClose} sx={{ color: darkMode ? "#fff" : "#000" }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      {/* <DialogContent
        dividers
        sx={{
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          "& .MuiTypography-root": {
            color: darkMode ? "#fff" : "#000",
          },
        }}
      ></DialogContent> */}

      <DialogContent
        dividers
        sx={{
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          "& .MuiTypography-root": {
            color: darkMode ? "#fff" : "#000",
          },
        }}
      >
        {/* ================= LEAD HEADER ================= */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            {leadsDetails?.lead?.name || "—"}
          </MDTypography>

          <MDBox display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
            <MDTypography
              variant="caption"
              sx={{
                backgroundColor: leadColors?.[leadsDetails?.lead?.lead_state] || "#9e9e9e",
                color: "#fff",
                px: 1.2,
                py: 0.4,
                borderRadius: "6px",
                textTransform: "uppercase",
              }}
            >
              {leadsDetails?.lead?.lead_state}
            </MDTypography>

            <MDTypography variant="caption">
              Assigned to: {leadsDetails?.lead?.assigned_to || "—"}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* ================= BASIC INFO ================= */}
        <MDBox mb={3}>
          <MDTypography variant="h6" mb={1}>
            Contact Details
          </MDTypography>

          <MDBox display="grid" gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={1}>
            <InfoItem label="Name" value={leadsDetails?.lead?.name} />

            <InfoItem label="Mobile" value={leadsDetails?.lead?.mobile} />

            <InfoItem label="Alternate Mobile" value={leadsDetails?.lead?.mobile2} />

            <InfoItem label="Email" value={leadsDetails?.lead?.email} />

            <InfoItem label="Budget" value={leadsDetails?.lead?.budget} />

            <InfoItem label="Purpose" value={leadsDetails?.lead?.purpose} />

            <InfoItem label="Preferred Location" value={leadsDetails?.lead?.preferredLocation} />

            <InfoItem label="Preferred City" value={leadsDetails?.lead?.preferredCity} />

            <InfoItem label="Assigned To" value={leadsDetails?.lead?.assigned_to} />

            <InfoItem label="Lead Status" value={leadsDetails?.lead?.lead_state} badge />
          </MDBox>
        </MDBox>

        {/* ================= CALL LOGS ================= */}
        {/* <MDBox>
          <MDTypography variant="h6" mb={2}>
            Call Logs
          </MDTypography>

          {leadsDetails?.callLogs && leadsDetails?.callLogs.length === 0 ? (
            <MDTypography variant="body2">No call logs found</MDTypography>
          ) : (
            <MDBox
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
              gap={2}
            >
              {leadsDetails?.callLogs &&
                leadsDetails?.callLogs.map((log) => <CallLogCard key={log._id} log={log} />)}
            </MDBox>
          )}
        </MDBox> */}
        <MDBox mt={2}>
          <Accordion
            // defaultExpanded
            sx={{
              backgroundColor: darkMode ? "#1f2937" : "#fff",
              color: darkMode ? "#fff" : "#000",
              borderRadius: 2,
              boxShadow: "none",
              border: `1px solid ${darkMode ? "#2c365f" : "#e0e0e0"}`,
              "&:before": { display: "none" },
            }}
          >
            {/* HEADER */}
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: darkMode ? "#fff" : "#000" }} />}
            >
              <MDTypography variant="h6">
                Call Logs ({leadsDetails?.callLogs?.length || 0})
              </MDTypography>
            </AccordionSummary>

            {/* CONTENT */}
            <AccordionDetails>
              {leadsDetails?.callLogs && leadsDetails?.callLogs.length === 0 ? (
                <MDTypography variant="body2">No call logs found</MDTypography>
              ) : (
                <MDBox
                  display="grid"
                  gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
                  gap={2}
                >
                  {leadsDetails?.callLogs?.map((log) => (
                    <CallLogCard key={log._id} log={log} />
                  ))}
                </MDBox>
              )}
            </AccordionDetails>
          </Accordion>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

LeadDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  leadId: PropTypes.any,
};

export default LeadDetailsModal;
